// Shared normalization primitives for the query layer.
//
// Per the react-query-options-migration design (decision 3): each leaf
// factory in lib/queries/ normalizes its raw args ONCE — trimming strings,
// sorting key-bearing arrays, filling defaults, converting Date→ISO — and
// uses that one normalized object for BOTH the cache key and the fetch call.
// These primitives are the building blocks domain factories reuse so the
// normalization is identical everywhere. There is deliberately NO wrapper
// around `queryOptions` (rejected: see "Rejected: the helper" in the doc).

/**
 * Returns a NEW sorted copy of `arr`. Stable for use in a cache key: callers
 * passing the same set in a different order produce the same key element,
 * closing the order-dependent drift in array-valued filters (member-list
 * `placeText`/`networks`/`industries`/`careerStages`/`openTo`, etc.).
 * Never mutates the input.
 */
export function sortedStrings(arr: readonly string[]): string[] {
  return [...arr].sort();
}

/** Trims and lowercases a string — the canonical form for case- and
 * whitespace-insensitive key fields (emails, free-text search/picker queries). */
export function trimLower(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Registry of every leaf factory's sample key, appended to by each domain WU.
 * The invariant test (`__tests__/query-keys.test.ts`) iterates this array and
 * asserts each `sampleKey` is JSON-serializable (decision 6: no Date / Map /
 * Set / function / class instance in a key). Starts empty; domain WUs push
 * `{ name, sampleKey }` entries as they migrate their factories.
 */
export type QueryKeyRegistryEntry = {
  /** Stable identifier for the factory, e.g. `'groupKeys.network.circles'`. */
  name: string;
  /** A representative `queryKey` the factory produces for some sample args. */
  sampleKey: readonly unknown[];
};

export const QUERY_KEY_REGISTRY: QueryKeyRegistryEntry[] = [];
